import { utils } from '@hh/global.js'

const { debounce } = utils

const DEBOUNCE_DELAY_MS = 100
const SLIDER_READY_MAX_ATTEMPTS = 20

class QuickFilters extends HTMLElement {
  constructor() {
    super()
    this.debouncedOnChange = debounce((event) => {
      this.handleCheckboxChange(event)
    }, DEBOUNCE_DELAY_MS)

    this._boundHandleKeydown = this._handleKeydown.bind(this)
    this._boundHandleFocus = this._handleFocus.bind(this)
    this._boundHandleBlur = this._handleBlur.bind(this)
  }

  connectedCallback() {
    this.filtersWrapper = document.querySelector('hh-filters-wrapper')
    this.form = document.getElementById('CollectionFiltersForm')
    this.checkboxes = this.querySelectorAll('[data-quick-filter-checkbox]')

    this.init()
    this.setupFilterSync()
  }

  setupFilterSync() {
    document.addEventListener('plp:filter-update', () => {
      requestAnimationFrame(() => {
        this.syncWithMainFilters()
      })
    })

    document.addEventListener('filters-rendered', () => {
      requestAnimationFrame(() => {
        this.syncWithMainFilters()
      })
    })

    const form = document.getElementById('CollectionFiltersForm')
    if (form) {
      form.addEventListener('change', (event) => {
        if (
          event.target &&
          event.target.hasAttribute('data-drawer-filter-checkbox')
        ) {
          requestAnimationFrame(() => {
            this.syncWithMainFilters()
          })
        }
      })

      form.addEventListener('input', (event) => {
        if (
          event.target &&
          event.target.hasAttribute('data-drawer-filter-checkbox')
        ) {
          requestAnimationFrame(() => {
            this.syncWithMainFilters()
          })
        }
      })
    }

    this.syncWithMainFilters()
  }

  syncWithMainFilters() {
    this.checkboxes = this.querySelectorAll('[data-quick-filter-checkbox]')

    this.checkboxes.forEach((quickCheckbox) => {
      const paramName =
        quickCheckbox.getAttribute('name') ||
        quickCheckbox.getAttribute('data-filter-param-name')
      const quickLabel = quickCheckbox.closest('[data-quick-filter-label]')

      if (!paramName || !quickLabel) return

      const values = this._extractFilterValues(quickCheckbox)
      const matchingCheckboxes = this._findMatchingDrawerCheckboxes(
        paramName,
        values
      )

      const hasMatching = matchingCheckboxes.length > 0

      this._updateQuickLabelVisibility(quickLabel, hasMatching)

      if (!hasMatching) return

      this._syncCheckboxState(quickCheckbox, values, matchingCheckboxes)
    })

    this._updateSplide()
  }

  _updateSplide() {
    const quickFilterFreeSlider = this.querySelector(
      'free-slider[data-quick-filter-slider]'
    )

    const splideInstance = quickFilterFreeSlider.slider

    splideInstance?.refresh()
  }

  _extractFilterValues(checkbox) {
    const filterValues =
      checkbox.getAttribute('data-filter-values') ||
      checkbox.getAttribute('value') ||
      ''
    return filterValues
      .split(',')
      .map((v) => this.normalizeValue(v.trim()))
      .filter(Boolean)
  }

  _findMatchingDrawerCheckboxes(paramName, values) {
    return values.flatMap((value) =>
      Array.from(
        document.querySelectorAll(
          `[data-drawer-filter-checkbox][name="${paramName}"][value]`
        )
      ).filter((cb) => this.normalizeValue(cb.value) === value)
    )
  }

  _updateQuickLabelVisibility(label, hasMatching) {
    const slide = label.closest('.splide__slide')
    label.hidden = !hasMatching
    if (slide) {
      slide.classList.toggle('is-hidden', !hasMatching)
    }
  }

  _syncCheckboxState(quickCheckbox, values, matchingCheckboxes) {
    const allDisabled = matchingCheckboxes.every((cb) => cb.disabled)
    quickCheckbox.disabled = allDisabled

    const allChecked = values.every((value) =>
      matchingCheckboxes.some(
        (cb) => this.normalizeValue(cb.value) === value && cb.checked === true
      )
    )

    quickCheckbox.checked = allChecked && values.length > 0

    this.updateLabelActiveState(quickCheckbox, quickCheckbox.checked)
  }

  _handleKeydown(event, checkbox) {
    if (
      [
        'ArrowRight',
        'ArrowDown',
        'ArrowLeft',
        'ArrowUp',
        'Home',
        'End',
        'Tab',
      ].includes(event.key)
    ) {
      this.isKeyboardNavigation = true
      this.handleKeyboardNavigation(event, checkbox)
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      checkbox.checked = !checkbox.checked
      const changeEvent = new Event('change', {
        bubbles: true,
        cancelable: true,
      })
      checkbox.dispatchEvent(changeEvent)
    }
  }

  _handleFocus(checkbox) {
    this.isKeyboardNavigation = false
  }

  _handleBlur(checkbox) {}

  attachEventListeners() {
    this.isKeyboardNavigation = false

    this.checkboxes.forEach((checkbox) => {
      checkbox.removeEventListener('change', this.debouncedOnChange)

      checkbox.addEventListener('change', (e) => {
        const target = e.target
        if (
          target &&
          target.hasAttribute('data-quick-filter-checkbox') &&
          target === checkbox
        ) {
          this.debouncedOnChange(e)
        }
      })
      checkbox.addEventListener('keydown', (e) =>
        this._handleKeydown(e, checkbox)
      )
      checkbox.addEventListener('focus', () => this._handleFocus(checkbox))
      checkbox.addEventListener('blur', () => this._handleBlur(checkbox))

      const label = checkbox.closest('[data-quick-filter-label]')
      if (label) {
        const oldHandler = label._quickFilterClickHandler
        if (oldHandler) {
          label.removeEventListener('click', oldHandler)
        }

        const handleLabelClick = (e) => {
          const correctCheckbox = label.htmlFor
            ? document.getElementById(label.htmlFor)
            : label.querySelector('[data-quick-filter-checkbox]')

          if (
            correctCheckbox &&
            correctCheckbox === checkbox &&
            correctCheckbox.hasAttribute('data-quick-filter-checkbox')
          ) {
            e.preventDefault()
            e.stopPropagation()

            correctCheckbox.checked = !correctCheckbox.checked

            const changeEvent = new Event('change', {
              bubbles: true,
              cancelable: true,
            })
            Object.defineProperty(changeEvent, 'target', {
              value: correctCheckbox,
              writable: false,
            })
            correctCheckbox.dispatchEvent(changeEvent)
          }
        }

        label._quickFilterClickHandler = handleLabelClick
        label.addEventListener('click', handleLabelClick)
      }
    })
  }

  init() {
    if (!this.filtersWrapper || !this.form) {
      return
    }

    this.attachEventListeners()
    this.syncWithMainFilters()
    this.configureSlider()
  }

  handleKeyboardNavigation(event, currentCheckbox) {
    const freeSlider = this.querySelector('free-slider')
    if (!freeSlider?.slider) return

    const currentSlide = currentCheckbox.closest('.splide__slide')
    if (!currentSlide) return

    const allSlides = Array.from(
      freeSlider.querySelectorAll('.splide__slide:not(.splide__slide--clone)')
    )
    const currentSlideIndex = allSlides.indexOf(currentSlide)
    if (currentSlideIndex === -1) return

    let targetSlideIndex = -1

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        targetSlideIndex =
          currentSlideIndex < allSlides.length - 1 ? currentSlideIndex + 1 : 0
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        targetSlideIndex =
          currentSlideIndex > 0 ? currentSlideIndex - 1 : allSlides.length - 1
        break
      case 'Home':
        event.preventDefault()
        targetSlideIndex = 0
        break
      case 'End':
        event.preventDefault()
        targetSlideIndex = allSlides.length - 1
        break
    }

    if (targetSlideIndex !== -1 && allSlides[targetSlideIndex]) {
      const targetSlide = allSlides[targetSlideIndex]
      const targetCheckbox = targetSlide.querySelector(
        '[data-quick-filter-checkbox]'
      )

      if (
        !targetCheckbox ||
        targetCheckbox.disabled ||
        targetCheckbox.getAttribute('data-has-valid-value') === 'false'
      ) {
        return
      }

      freeSlider.slider.go(targetSlideIndex)

      targetCheckbox.focus()
    }
  }

  waitForSliderReady(
    freeSlider,
    callback,
    maxAttempts = SLIDER_READY_MAX_ATTEMPTS
  ) {
    let attempts = 0

    const checkSlider = () => {
      if (freeSlider.slider) {
        callback()
      } else {
        attempts++
        if (attempts < maxAttempts) {
          requestAnimationFrame(checkSlider)
        }
      }
    }

    requestAnimationFrame(checkSlider)
  }

  configureSlider() {
    const freeSlider = this.querySelector('free-slider')
    if (!freeSlider) return

    this.waitForSliderReady(freeSlider, () => {
      freeSlider.slider.options = {
        ...freeSlider.slider.options,
        type: 'slide',
        gap: 10,
        autoWidth: true,
        arrows: true,
        padding: 0,
      }

      freeSlider.slider.refresh()

      this.setupKeyboardOnlyArrows(freeSlider)

      this.dispatchEvent(
        new CustomEvent('quick-filters-slider-ready', {
          bubbles: true,
          detail: { slider: freeSlider },
        })
      )
    })
  }

  setupKeyboardOnlyArrows(freeSlider) {
    const prevButton = freeSlider.querySelector('.splide__arrow--prev')
    const nextButton = freeSlider.querySelector('.splide__arrow--next')

    if (!prevButton || !nextButton) return

    const disableClick = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }

    prevButton.addEventListener('click', disableClick)
    nextButton.addEventListener('click', disableClick)

    this._keyboardArrowsCleanup = () => {
      prevButton.removeEventListener('click', disableClick)
      nextButton.removeEventListener('click', disableClick)
    }
  }

  updateLabelActiveState(checkbox, isChecked) {
    const label = checkbox.closest('[data-quick-filter-label]')
    label?.classList.contains(
      'collection-quick-filters__checkbox-label--all-watches'
    ) &&
      label.classList.toggle(
        'collection-quick-filters__checkbox-label--active',
        isChecked
      )
  }

  normalizeValue(raw) {
    const trimmed = raw.trim()
    try {
      return decodeURIComponent(trimmed.replace(/\+/g, ' '))
    } catch (e) {
      return trimmed.replace(/\+/g, ' ')
    }
  }

  handleCheckboxChange(event) {
    let checkbox =
      event.currentTarget &&
      event.currentTarget.hasAttribute('data-quick-filter-checkbox')
        ? event.currentTarget
        : event.target

    if (
      !checkbox ||
      checkbox.tagName !== 'INPUT' ||
      checkbox.type !== 'checkbox' ||
      !checkbox.hasAttribute('data-quick-filter-checkbox')
    ) {
      const clickedElement = event.target
      const label = clickedElement?.closest('[data-quick-filter-label]')
      if (label) {
        if (label.htmlFor) {
          checkbox = document.getElementById(label.htmlFor)
        } else {
          checkbox = label.querySelector('[data-quick-filter-checkbox]')
        }
      }

      if (!checkbox || !checkbox.hasAttribute('data-quick-filter-checkbox')) {
        return
      }
    }

    const hasValidValue = checkbox.getAttribute('data-has-valid-value')
    if (hasValidValue === 'false') {
      event.preventDefault()
      checkbox.checked = false
      return
    }

    if (checkbox.disabled) {
      event.preventDefault()
      checkbox.checked = false
      return
    }

    const paramName =
      checkbox.getAttribute('name') ||
      checkbox.getAttribute('data-filter-param-name')
    let filterValues = checkbox.getAttribute('data-filter-values')
    if (!filterValues || filterValues === '') {
      filterValues = checkbox.getAttribute('value')
    }
    const isChecked = checkbox.checked

    if (!paramName || !filterValues) return

    const mainCheckboxes = document.querySelectorAll(
      `[data-drawer-filter-checkbox][name="${paramName}"]`
    )

    const values = filterValues
      .split(',')
      .map((v) => this.normalizeValue(v.trim()))
      .filter((v) => v)

    const checkboxesToUpdate = []

    mainCheckboxes.forEach((cb) => {
      const cbValue = cb.value
      const normalizedCbValue = this.normalizeValue(cbValue)

      if (values.includes(normalizedCbValue)) {
        checkboxesToUpdate.push(cb)
      }
    })

    if (checkboxesToUpdate.length > 0) {
      const form = checkboxesToUpdate[0].closest('form')

      checkboxesToUpdate.forEach((cb) => {
        cb.checked = isChecked
      })

      if (form) {
        const inputEvent = new Event('input', {
          bubbles: true,
          cancelable: true,
        })
        form.dispatchEvent(inputEvent)
      }
    }
  }

  disconnectedCallback() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.removeEventListener('change', this.debouncedOnChange)
    })

    if (this._keyboardArrowsCleanup) {
      this._keyboardArrowsCleanup()
    }
  }
}

if (!customElements.get('quick-filters')) {
  customElements.define('quick-filters', QuickFilters)
}
